import {
  apiErrorSlug,
  deleteOrphanSession,
  estimateWorkoutCalories,
  isApiErrorLike,
  isZeroWorkSet,
  keyForOutboxItem,
  mintSetIds,
  persistWorkout,
  sessionVersion,
  unwrapSaveError,
  type AthleteEnergy,
  type EffortScale,
  type MovementPattern,
  type Workout as WorkoutT,
  type WorkoutSet,
} from '@heraion/shared';

import { uuidv4 } from './uuid';
import type { LoggerDraft } from './state/loggerDraft';
import { snapshotRecentEvidence } from './useEntryHistory';
import { buildPayload } from './workoutMapping';
import type { LoggerMode } from './loggerMode';
import type { EditingTarget, MaxBumpInput } from './LoggerScreen';
import type { LoggerHostDeps, LoggerWriter, QueuedWorkout } from './hostDeps';
import { openSaveWriter, saveFailureMessage } from './saveWriter';

// THE one way a built workout reaches the server or the queue, without React
// (#445): the logger's Save (useSaveWorkout) and the idle watch's save both
// come here. What the athlete sees after it is the caller's.

/** #418: a 409 `duplicate_id` that came before any set: the session POST's,
 *  never wrapped in a SessionLeftError (that one means the session landed in
 *  THIS attempt). */
const isLeftOverSession = (err: unknown): boolean =>
  isApiErrorLike(err) && err.status === 409 && apiErrorSlug(err) === 'duplicate_id';

export type BuiltPayload = Extract<ReturnType<typeof buildPayload>, { ok: true }>;

// LG-28: the shared rule the server refuses a set by, read on the built set.
const recordsNoWork = (s: WorkoutSet): boolean =>
  isZeroWorkSet({
    kind: s.type,
    reps: 'reps' in s ? s.reps : undefined,
    weight: 'weight' in s ? s.weight : undefined,
  });

/** The save's build, for both paths (the athlete's Save, the idle watch):
 *  only the completed sets, the suggested name, and a 0 x 0 set refused here
 *  in words, not at the server. The other readers of buildPayload keep it. */
export function buildSavePayload(
  draft: LoggerDraft,
  effortScale: EffortScale,
  fallbackName: string,
): ReturnType<typeof buildPayload> {
  const built = buildPayload(draft, effortScale, { dropUncompleted: true, fallbackName });
  if (built.ok && built.payload.entries.some((e) => e.sets.some(recordsNoWork))) {
    return { ok: false, reason: 'mobile.logger.errZeroWorkSet' };
  }
  return built;
}

export type SaveDraftCtx = {
  draft: LoggerDraft;
  editing: EditingTarget | null;
  mode: LoggerMode;
  /** #38: the correction's session is gone, so the open draft is saved as a
   *  NEW workout, exactly like a fresh log: no edit reference. */
  asNew: boolean;
  athlete: AthleteEnergy;
  deps: LoggerHostDeps;
  /** useFlushNoteEvents: the timeline notes, written once the workout is. */
  flushNoteEvents: (sessionId: string, writer: LoggerWriter, asNew?: boolean) => Promise<string[]>;
};

export type SaveDraftOutcome =
  /** Nobody signed in: nothing was sent, the draft stays. */
  | { kind: 'notSignedIn'; error: string }
  | { kind: 'sent'; sessionId: string; maxBumpInput: MaxBumpInput; failedNotes: string[] }
  /** In the queue. `carded` (#314): into a recovery card instead. */
  | { kind: 'queued'; sessionId: string; maxBumpInput: MaxBumpInput; carded: boolean }
  /** Neither sent nor queued: the draft stays and its next save starts over. */
  | { kind: 'notQueued'; error: string }
  /** A deterministic server verdict. The session it left is deleted. */
  | { kind: 'refused'; err: unknown };

export async function saveDraft(
  built: BuiltPayload,
  { draft, editing, mode, asNew, athlete, deps, flushNoteEvents }: SaveDraftCtx,
): Promise<SaveDraftOutcome> {
  // #42: one account for the send and the queued row (./saveWriter).
  const opened = openSaveWriter(deps);
  if ('error' in opened) return { kind: 'notSignedIn', error: opened.error };
  const { writer } = opened;
  // The draft's id (#418): a new workout's session id, the same on every
  // save of this draft, and the seed of the queued row's replay keys either
  // way. `?? uuidv4()` only for a draft that never met initial() or
  // hydrateDraft.
  const id = draft.sessionId ?? uuidv4();
  // On an edit, carry the original session's createdAt on the payload.
  // A correction rewrites the session in place (#38) and its PATCH does
  // not send createdAt, so the server keeps its own either way. Repeat
  // clones into a genuinely new session and keeps a fresh createdAt.
  // `mode === 'log-past'` already implies a non-null editing target (see
  // deriveLoggerMode), but the compiler cannot see through the derivation:
  // the null check narrows `editing` for the reads below, it does not
  // re-derive the mode.
  // ⛔ NO `mode === 'prescribe'` branch here, deliberately (PE-22 K0), and
  // the omission is the declaration. A prescription is never saved through
  // this path: it is authored into the PROGRAM payload (`SetIntensity`, via
  // the mount's onPrescribeEffort, see useEffortCommit), not POSTed as a
  // Workout. So the question to answer in 'prescribe' is "how did you get
  // here", and a local guard would answer it by swallowing the wiring
  // mistake that brought us: the failure mode the house already named one
  // floor down (useSetCyclePill, same shape).
  // [[feedback_a_fail_closed_guard_hides_the_bug_above_it]]
  const isEdit = !asNew && editing !== null && mode === 'log-past';
  // A correction keeps the session's id (#38, model A).
  const sessionId = isEdit ? editing.sessionId : id;
  const payload =
    isEdit && editing.workout.createdAt
      ? { ...built.payload, createdAt: editing.workout.createdAt }
      : built.payload;
  // Movement pattern per exercise (from the draft entries, populated at
  // picker time): used both for the HC-5 calorie estimate here and the
  // post-save MAX-bump eval below, so build it once.
  const exercisePatterns: Record<string, MovementPattern | null> = {};
  for (const e of draft.entries) {
    exercisePatterns[e.exerciseId] = e.movementPattern;
  }
  // HC-5: compute JL's own calorie estimate at save (uses the athlete's
  // then-current bodyweight) and persist it beside the measured value. Null
  // when nothing is estimable: then the field is simply absent.
  const caloriesEstimate = estimateWorkoutCalories(
    built.payload,
    exercisePatterns,
    athlete,
  );
  const workoutToPersist = caloriesEstimate
    ? { ...payload, caloriesEstimate }
    : payload;
  // A new workout's set ids, minted once: the direct save and the queued
  // row send the same ones, so a replay never stores a set twice (#286).
  // A correction carries the version the editor loaded (#314). Built on a
  // queued one, it takes the version that one left once it landed; until
  // then (`turn` null) it waits behind it, in the queue.
  const turn =
    isEdit && editing.queuedOn !== undefined
      ? (writer.correctionTurn?.(editing.queuedOn) ?? null)
      : undefined;
  // #289: the saved notes this correction removed, for a fold into a card.
  const kept = new Set((draft.noteEvents ?? []).map((ev) => ev.id));
  const notesRemoved = isEdit
    ? (editing.notes ?? []).filter((n) => !kept.has(n.id)).map((n) => n.performedAt)
    : [];
  const queued: QueuedWorkout = isEdit
    ? {
        id,
        workout: workoutToPersist,
        editingId: editing.sessionId,
        baseVersion: turn ? turn.baseVersion : sessionVersion(editing.workout),
        supersedes: turn === null ? editing.queuedOn : undefined,
        // #289: what a recovery of this correction puts back in the draft.
        ...(draft.noteEvents?.length ? { noteEvents: draft.noteEvents } : {}),
        ...(notesRemoved.length > 0 ? { notesRemoved } : {}),
      }
    : { id, workout: workoutToPersist, editingId: null, setIds: mintSetIds(workoutToPersist) };
  // Build a Workout-shaped object from the about-to-be-saved payload for
  // the post-save MAX-bump evaluation. Server-stamped fields (profileId,
  // createdAt, updatedAt) get placeholder values: the evaluator only
  // walks `entries[].sets`, so the placeholders are immaterial. The
  // exercise pattern map comes from the draft entries (populated at
  // picker time, see DraftEntry); entries without a pattern fall through
  // to the default rep-trust cap in the MAX-suggest functions.
  //
  // ⚠️ Built HERE, above the try, and not in the success branch: the
  // offline branch needs the identical object (LG-35: a workout saved in
  // airplane mode used to offer no MAX prompt at all, because
  // onSaved('queued') carried no maxBumpInput). Nothing in it depends on
  // the server's answer, so one construction serves both paths. Two
  // copies of this field list would be the classic pair that diverges.
  const justSavedWorkout: WorkoutT = {
    id: sessionId,
    profileId: '',
    startedAt: built.payload.startedAt,
    ...(built.payload.endedAt ? { endedAt: built.payload.endedAt } : {}),
    ...(built.payload.name ? { name: built.payload.name } : {}),
    entries: built.payload.entries,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
  // Snapshot the recent best e1RM per exercise BEFORE either branch
  // invalidates the history cache: the post-save MAX-regression gate
  // needs the pre-save window (recent PAST sessions, not the just-saved
  // one) so one light day can't lower a MAX below a recent stronger
  // session (Antenore 2026-06-26). Cold cache: empty map, legacy
  // behaviour.
  // `MX-11`: ONE walk gives both halves, the best and how many separate
  // sessions produced one. Splitting them into two snapshots would walk the
  // same window twice and let the two disagree about what qualified.
  const recentEvidence = snapshotRecentEvidence(Date.now());
  const recentBestE1RM: Record<string, number> = {};
  const recentSessionCount: Record<string, number> = {};
  for (const [slug, ev] of Object.entries(recentEvidence)) {
    recentBestE1RM[slug] = ev.bestE1RM;
    recentSessionCount[slug] = ev.sessions;
  }
  const maxBumpInput: MaxBumpInput = {
    workout: justSavedWorkout,
    exercisePatterns,
    workoutId: sessionId,
    recentBestE1RM,
    recentSessionCount,
  };
  // ONE place for "the notes' flush never changes the save's outcome": by
  // the time it runs the workout is already sent or queued, so a throw here
  // must not turn into notQueued or a second queueing (#418).
  // Returns the texts of the notes that did not save (#511).
  const flushNotes = async (): Promise<string[]> => {
    try {
      return await flushNoteEvents(sessionId, writer, asNew);
    } catch (e) {
      console.warn('note flush failed', e);
      return [];
    }
  };
  // The queued path. A session a failed direct save left is deleted unless
  // the queue took it (#289).
  const queueSave = async (
    dropLeftSession: () => Promise<void>,
  ): Promise<SaveDraftOutcome> => {
    // Enqueue with the same id so a future drain replays through the
    // API's idempotency middleware with stable keys derived from it.
    let inQueue = false;
    try {
      const carded = (await writer.queueWorkout(queued)) === 'carded';
      inQueue = true;
      // Queue the notes too (same session id) so they ride the same
      // reconnect drain as the offline workout, before the reset.
      // The texts of a failed note are dropped: a queued save has no summary
      // to show them in (#511), a declared silent loss on the offline path.
      void (await flushNotes());
      return { kind: 'queued', sessionId, maxBumpInput, carded };
    } catch (enqueueErr) {
      // Not queued: the draft stays and its next save starts over.
      if (!inQueue) await dropLeftSession();
      return { kind: 'notQueued', error: saveFailureMessage(enqueueErr) };
    }
  };
  // Built on a correction still queued (#314): it waits behind that one.
  if (queued.supersedes !== undefined) return queueSave(async () => {});
  try {
    // Try direct first, the same model the web uses. On ApiError (server
    // replied with a 4xx/5xx) the caller surfaces the actual server
    // message, instead of a generic "retry when online" that no retry will
    // ever clear.
    const send = () =>
      persistWorkout({
        workout: workoutToPersist,
        editingId: queued.editingId,
        baseVersion: queued.baseVersion,
        apiFetch: writer.apiFetch,
        generateId: () => id,
        setIds: queued.setIds,
        // A correction's key is its queued row's (#314): a replay after a
        // lost answer gets the answer, not a 412 on its own write.
        ...(isEdit ? { idempotencyKeyFor: keyForOutboxItem(id) } : {}),
        // The catch below decides about a session a failure left (#289).
        cleanupByCaller: true,
      });
    try {
      await send();
    } catch (first) {
      // #418: the draft's id is stable, so a 409 `duplicate_id` on a new
      // workout's session POST is what an earlier save of this draft left
      // there: an answer lost on the way back, or a cleanup DELETE that
      // failed. What it holds is not known (an older envelope, some of the
      // sets), so it is deleted and the draft is posted once more, whole. A
      // second failure takes the branches below like any other.
      if (isEdit || !isLeftOverSession(first)) throw first;
      await deleteOrphanSession(writer.apiFetch, id);
      await send();
    }
    // Persist the timeline notes against the saved session id, before
    // the draft reset wipes them.
    const failedNotes = await flushNotes();
    return { kind: 'sent', sessionId, maxBumpInput, failedNotes };
  } catch (thrown) {
    // A session the failed save left on the server is deleted unless the
    // queue takes the save, whose replay completes it by id (#289).
    const { error: err, leftSession } = unwrapSaveError(thrown);
    const dropLeftSession = async () => {
      if (leftSession !== null) await deleteOrphanSession(writer.apiFetch, leftSession);
    };
    // ⭐ LG-31: the default is QUEUE, and the direction is the fix.
    // This used to read `if (isNetworkError(err))`, i.e. queue only when
    // we were POSITIVE it was the network. But isNetworkError opens by
    // rejecting every ApiError, and offline apiFetch throws a synthetic
    // ApiError(401, 'not_authenticated') as soon as getIdToken() can't
    // reach Cognito to refresh, so the queue branch was skipped and the
    // athlete got a server error for a workout the server never saw.
    // Leonardo hit exactly this on stable 4.13.1 (2026-08-07).
    // Now: queue unless a server actually returned a deterministic
    // verdict. A queue too many costs one idempotent replay; a queue too
    // few costs a training session.
    if (deps.shouldQueueError(err)) return queueSave(dropLeftSession);
    // A deterministic server verdict (400/404/422…). Replaying won't clear
    // it, so the caller shows the actual server message.
    //
    // ⚠️ This branch also takes 409 duplicate_id, and that is deliberate:
    // do NOT align it with writeOrQueue, where 409 means `done`. That
    // reading holds for a single-request write; a workout is two.
    // persistWorkout flips `sessionPosted` only after the session POST
    // resolves, so a 409 there means the sets loop never ran and this
    // attempt wrote no sets: "already exists" is not "saved". A new
    // workout's first 409 never gets here: the try above deletes the
    // session left by an earlier save of the draft and posts again (#418),
    // so a 409 here is the second one, the DELETE having failed too. A
    // correction's 409 is a SessionConflictError (#38). Pinned in
    // useSaveWorkout.test.tsx.
    // A refused set too (#289): the draft stays whole, and is saved again.
    await dropLeftSession();
    return { kind: 'refused', err };
  }
}
